/**
 * KT Product Enhancements - Standalone Module
 * Only loads on product pages and doesn't interfere with existing functionality
 */

(function() {
  'use strict';
  
  // Only run on product pages
  if (!window.location.pathname.includes('/products/')) {
    return;
  }
  
  class KTProductEnhancements {
    constructor() {
      this.init();
    }
    
    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupEnhancements());
      } else {
        this.setupEnhancements();
      }
    }
    
    setupEnhancements() {
      try {
        this.setupLoyaltyWidget();
        this.setupEnhancedGallery();
        this.setupRecommendations();
      } catch (error) {
        console.warn('KT Product Enhancements: Error setting up features:', error);
      }
    }
    
    // Loyalty Widget
    setupLoyaltyWidget() {
      const loyaltyWidget = document.querySelector('[data-kt-loyalty-widget]');
      if (!loyaltyWidget) return;
      
      const redeemBtn = loyaltyWidget.querySelector('[data-kt-loyalty-redeem]');
      const historyBtn = loyaltyWidget.querySelector('[data-kt-loyalty-history]');
      const progressBar = loyaltyWidget.querySelector('[data-kt-loyalty-progress]');
      
      if (redeemBtn) {
        redeemBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleLoyaltyRedeem();
        });
      }

      if (historyBtn) {
        // KT-234: toggle the panel the widget now renders, instead of navigating to
        // '/account#loyaltyActivityList'. That URL 302s to account.kenttraders.co.uk (new
        // customer accounts), where the theme does not run — so the old handler took the
        // customer off-site and showed nothing, which is the reported "doesn't open".
        // See the comment block in snippets/kt-loyalty-widget-modern.liquid.
        const panel = loyaltyWidget.querySelector('#ktLoyaltyWidgetHistory');
        historyBtn.addEventListener('click', () => {
          if (!panel) return;
          const willOpen = panel.hasAttribute('hidden');
          if (willOpen) panel.removeAttribute('hidden');
          else panel.setAttribute('hidden', '');
          historyBtn.setAttribute('aria-expanded', String(willOpen));
        });
      }
      
      // Keep the server-rendered AIMarketOps progress value authoritative.
      if (progressBar) {
        const progress = progressBar.getAttribute('data-progress') || 0;
        const progressFill = progressBar.querySelector('.kt-loyalty-widget__progress-fill');
        if (progressFill) progressFill.style.width = `${progress}%`;
      }
    }
    
    async handleLoyaltyRedeem() {
      const loyaltyWidget = document.querySelector('[data-kt-loyalty-widget]');
      if (!loyaltyWidget) return;

      const redeemBtn = loyaltyWidget.querySelector('[data-kt-loyalty-redeem]');
      const status = loyaltyWidget.querySelector('[data-kt-loyalty-status]');
      const rewards = Number.parseInt(loyaltyWidget.dataset.availableRewards || '0', 10) || 0;
      // KT-218: the economics come from the widget, not from literals here. This block used
      // to say "at least 100 points" and offer `rewards * 100` points for £`rewards`, which
      // was the retired 100pt/£1 rate — so after 2026-08-14 it overstated the threshold 4x
      // and misquoted both the points cost and the £ value in a confirmation dialog.
      const perReward = Number.parseInt(loyaltyWidget.dataset.pointsPerReward || '25', 10) || 25;
      const rewardValue = Number.parseFloat(loyaltyWidget.dataset.rewardValue || '0.25') || 0.25;
      const money = (n) => `£${n.toFixed(2)}`;
      const showStatus = (message) => {
        if (!status) return;
        status.hidden = false;
        status.textContent = message;
      };

      try {
        if (rewards < 1) {
          showStatus(`You need at least ${perReward} points to redeem a reward.`);
          return;
        }

        if (
          !window.confirm(
            `Redeem ${rewards * perReward} points for a ${money(rewards * rewardValue)} discount code?`
          )
        ) {
          return;
        }

        if (redeemBtn) redeemBtn.disabled = true;
        showStatus('Redeeming…');

        const response = await fetch('/apps/loyalty/redeem', {
          method: 'POST',
          headers: { Accept: 'application/json' }
        });
        const result = await response.json().catch(() => ({}));

        if (response.ok && result.ok && result.code) {
          showStatus(`Applying £${result.value} to your cart…`);
          window.location.href = `/discount/${encodeURIComponent(result.code)}?redirect=/cart`;
          return;
        }

        if (result.error === 'not_logged_in') {
          showStatus('Please sign in again to redeem your points.');
        } else if (result.error === 'insufficient_balance') {
          showStatus('Not quite enough points to redeem yet.');
        } else {
          showStatus('Could not redeem right now — please try again shortly.');
        }
      } catch (error) {
        console.error('Error handling loyalty redemption:', error);
        showStatus('Could not reach the rewards service — please try again.');
      } finally {
        if (redeemBtn) redeemBtn.disabled = false;
      }
    }
    
    // Enhanced Gallery
    setupEnhancedGallery() {
      const gallery = document.querySelector('[data-kt-enhanced-gallery]');
      if (!gallery) return;
      
      const mainImage = gallery.querySelector('[data-kt-gallery-main]');
      const thumbnails = gallery.querySelectorAll('[data-kt-gallery-thumb]');
      const zoomBtn = gallery.querySelector('[data-kt-gallery-zoom]');
      
      // Thumbnail click handling
      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const imgSrc = thumb.getAttribute('data-src');
          if (imgSrc && mainImage) {
            mainImage.src = imgSrc;
            mainImage.alt = thumb.getAttribute('data-alt') || '';
            
            // Update active thumbnail
            thumbnails.forEach(t => t.classList.remove('is-active'));
            thumb.classList.add('is-active');
          }
        });
      });
      
      // Zoom functionality
      if (zoomBtn && mainImage) {
        zoomBtn.addEventListener('click', () => {
          this.openImageModal(mainImage.src, mainImage.alt);
        });
      }
    }
    
    openImageModal(src, alt) {
      const modal = document.createElement('div');
      modal.className = 'kt-image-modal';
      modal.innerHTML = `
        <div class="kt-image-modal__overlay"></div>
        <div class="kt-image-modal__content">
          <button class="kt-image-modal__close">&times;</button>
          <img src="${src}" alt="${alt}" class="kt-image-modal__image">
        </div>
      `;
      
      // Add modal styles
      const style = document.createElement('style');
      style.textContent = `
        .kt-image-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kt-image-modal__overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.8);
        }
        .kt-image-modal__content {
          position: relative;
          max-width: 90%;
          max-height: 90%;
        }
        .kt-image-modal__image {
          width: 100%;
          height: auto;
          border-radius: 8px;
        }
        .kt-image-modal__close {
          position: absolute;
          top: -40px;
          right: 0;
          background: white;
          border: none;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `;
      
      document.head.appendChild(style);
      document.body.appendChild(modal);
      
      // Close modal functionality
      const closeModal = () => {
        document.body.removeChild(modal);
        document.head.removeChild(style);
      };
      
      modal.querySelector('.kt-image-modal__close').addEventListener('click', closeModal);
      modal.querySelector('.kt-image-modal__overlay').addEventListener('click', closeModal);
      
      // Close on escape key
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          closeModal();
          document.removeEventListener('keydown', handleEscape);
        }
      };
      document.addEventListener('keydown', handleEscape);
    }
    
    // Recommendations
    setupRecommendations() {
      const recommendations = document.querySelector('[data-kt-recommendations]');
      if (!recommendations) return;
      
      const prevBtn = recommendations.querySelector('[data-kt-recommendations-prev]');
      const nextBtn = recommendations.querySelector('[data-kt-recommendations-next]');
      const items = recommendations.querySelectorAll('[data-kt-recommendation-item]');
      
      let currentIndex = 0;
      
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          currentIndex = Math.max(0, currentIndex - 1);
          this.updateRecommendationsDisplay(items, currentIndex);
        });
      }
      
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          currentIndex = Math.min(items.length - 1, currentIndex + 1);
          this.updateRecommendationsDisplay(items, currentIndex);
        });
      }
      
      // Initialize display
      this.updateRecommendationsDisplay(items, currentIndex);
    }
    
    updateRecommendationsDisplay(items, currentIndex) {
      items.forEach((item, index) => {
        if (index >= currentIndex && index < currentIndex + 3) {
          item.style.display = 'block';
        } else {
          item.style.display = 'none';
        }
      });
    }
    
  }
  
  // Initialize enhancements
  new KTProductEnhancements();
  
})();
